import React, { memo } from "react";
import { Box, Grid, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { ReactComponent as UploadIcon } from "@assets/svg/wire-upload-document.svg";
import { ReactComponent as PencilIcon } from "@assets/svg/wire-fill-manually.svg";

interface Props {
  onFillManually(): void;
  onUploadDocument(): void;
}

interface OptionCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  onSelect(): void;
  classes: ReturnType<typeof useStyles>;
}

const useStyles = makeStyles((theme) => ({
  introHeading: {
    color: theme.palette.primary.main,
    fontSize: 22,
    lineHeight: "22px",
    fontWeight: 500,
    letterSpacing: "0.02em",
    marginBottom: theme.spacing(2),
  },
  introBody: {
    color: theme.palette.primary.main,
    fontSize: 16,
    lineHeight: "22px",
    fontWeight: 400,
  },
  optionCard: {
    appearance: "none",
    backgroundColor: theme.palette.common.white,
    border: "none",
    color: theme.palette.primary.main,
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    font: "inherit",
    minHeight: 260,
    padding: theme.spacing(8, 3),
    textAlign: "center",
    width: "100%",
    "&:hover $iconWrapper, &:focus-visible $iconWrapper": {
      backgroundColor: theme.palette.primary.main,
    },
    "&:hover $iconWrapper svg path, &:hover $iconWrapper svg line, &:focus-visible $iconWrapper svg path, &:focus-visible $iconWrapper svg line":
      {
        fill: theme.palette.common.white,
        stroke: theme.palette.common.white,
      },
  },
  optionIcon: {
    height: 43,
    width: 40,
  },
  pencilIcon: {
    height: 53,
    width: 70,
  },
  iconWrapper: {
    width: 118,
    height: 118,
    borderRadius: "50%",
    backgroundColor: theme.palette.grey[200],
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: theme.spacing(6),
  },
  optionTitle: {
    color: theme.palette.primary.main,
    fontSize: 22,
    lineHeight: "22px",
    fontWeight: 500,
    letterSpacing: "0.02em",
    marginBottom: theme.spacing(2),
  },
  optionDescription: {
    color: theme.palette.primary.main,
    fontSize: 16,
    lineHeight: "22px",
    fontWeight: 400,
    letterSpacing: "0.02em",
  },
}));

const OptionCard = ({ icon, title, description, onSelect, classes }: OptionCardProps) => (
  <Box component="button" type="button" className={classes.optionCard} onClick={onSelect}>
    <Box className={classes.iconWrapper}>{icon}</Box>
    <Typography component="span" className={classes.optionTitle}>
      {title}
    </Typography>
    <Typography component="span" className={classes.optionDescription}>
      {description}
    </Typography>
  </Box>
);

const WireInstructionOptionsDialog = ({ onFillManually, onUploadDocument }: Props) => {
  const classes = useStyles();

  return (
    <>
      <div data-testid="wire-instructions-options-dialog" />
      <Grid item xs={12}>
        <Typography className={classes.introHeading}>How would you like to provide your wire information?</Typography>
        <Typography className={classes.introBody}>
          You can upload a document with your bank details or fill in the form manually.
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={7.5} alignItems="stretch">
          <Grid item xs={12} md={6}>
            <OptionCard
              classes={classes}
              icon={<UploadIcon className={classes.optionIcon} aria-hidden />}
              title="Upload a Document"
              description="PDF, image, or bank letter"
              onSelect={onUploadDocument}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <OptionCard
              classes={classes}
              icon={<PencilIcon className={classes.pencilIcon} aria-hidden />}
              title="Fill in Manually"
              description="Enter your wire details by hand"
              onSelect={onFillManually}
            />
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default memo(WireInstructionOptionsDialog);
